import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/design-system/components/Button/Button';
import { useConnectivity } from '@/offline/useConnectivity';
import { useSyncStatus } from '@/offline/useSyncStatus';
import { requeueFailedMutations, type UnsyncedSummary } from '@/offline/db';

/**
 * Logout guard (BUGS #21): logging out deletes this device's offline data so
 * the next person on a shared tablet can't inherit it — but never silently.
 * Staying signed in is the default; a best-effort "sync first" is offered
 * when it could actually help.
 */
export function LogoutConfirmDialog({ summary, onStay, onDelete, onSynced }: {
  summary: UnsyncedSummary;
  onStay: () => void;
  /** Destroy local data and log out. */
  onDelete: () => Promise<void> | void;
  /** Re-check after a sync attempt; caller logs out if the queue drained. */
  onSynced: () => Promise<void>;
}) {
  const { isOnline } = useConnectivity();
  const { triggerSync, isSyncing } = useSyncStatus();
  const [busy, setBusy] = useState(false);
  const [syncTried, setSyncTried] = useState(false);

  const n = summary.items > 0 ? summary.items : summary.blobs;
  const noun = summary.items > 0
    ? `unsynced item${summary.items === 1 ? '' : 's'}`
    : `unsent photo${summary.blobs === 1 ? '' : 's'}`;

  async function syncThenLogout() {
    setBusy(true);
    setSyncTried(true);
    try {
      await requeueFailedMutations(); // failed items get one honest last try too
      await triggerSync();
    } finally {
      setBusy(false);
    }
    await onSynced(); // logs out when drained; otherwise the dialog refreshes
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" onClick={onStay}>
      <div
        role="alertdialog"
        aria-label="Unsynced work warning"
        className="w-full max-w-md rounded-t-2xl bg-graphite-900 p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 shrink-0 text-amber-400" />
          <h2 className="text-lg font-semibold text-graphite-100">Log out?</h2>
        </div>
        <p className="text-sm text-graphite-200">
          You have {n} {noun}. Logging out deletes {n === 1 ? 'it' : 'them'} from this
          device permanently. Sync first if you can.
        </p>
        {syncTried && (
          <p className="mt-2 text-sm text-amber-300" role="status">
            Some items couldn&rsquo;t sync — they&rsquo;ll be deleted if you log out.
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2">
          {/* Staying is the default and must never be blocked. */}
          <Button size="lg" className="w-full" autoFocus onClick={onStay}>
            Stay signed in
          </Button>
          {isOnline && summary.items > 0 && (
            <Button size="lg" variant="secondary" className="w-full" onClick={syncThenLogout} disabled={busy || isSyncing}>
              {busy || isSyncing ? 'Syncing…' : 'Sync now, then log out'}
            </Button>
          )}
          <button
            onClick={() => onDelete()}
            disabled={busy || isSyncing}
            className="h-12 w-full rounded-lg border border-danger-500/50 text-sm font-medium text-danger-400 hover:bg-danger-500/10 disabled:opacity-50"
          >
            Log out and delete
          </button>
        </div>
      </div>
    </div>
  );
}
